import React, { useState } from 'react';
import { X } from 'lucide-react';
import type { SuretyBondingSettings } from '../../types/regulatory';
import FieldHelp from './FieldHelp';

interface SuretyBondingSettingsModalProps {
  initial: SuretyBondingSettings;
  onClose: () => void;
  onSave: (settings: SuretyBondingSettings) => Promise<void>;
}

const SuretyBondingSettingsModal: React.FC<SuretyBondingSettingsModalProps> = ({ initial, onClose, onSave }) => {
  const [agency, setAgency] = useState(initial.agent.agency);
  const [contact, setContact] = useState(initial.agent.contact);
  const [email, setEmail] = useState(initial.agent.email);
  const [phone, setPhone] = useState(initial.agent.phone);
  const [address, setAddress] = useState(initial.agent.address);
  const [signerName, setSignerName] = useState(initial.signer.name);
  const [signerTitle, setSignerTitle] = useState(initial.signer.title);
  const [bidPercent, setBidPercent] = useState(String(initial.defaultBidBondPercent));
  const [warrantyMonths, setWarrantyMonths] = useState(String(initial.defaultWarrantyMonths));
  const [bondForm, setBondForm] = useState(initial.defaultBondForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    const pct = Number(bidPercent);
    const months = Number(warrantyMonths);
    if (!Number.isFinite(pct) || pct <= 0 || pct > 100) {
      setError('Default bid bond percent must be between 0 and 100.');
      return;
    }
    if (!Number.isFinite(months) || months < 0) {
      setError('Warranty months must be zero or more.');
      return;
    }
    if (!signerName.trim()) {
      setError('Signer name is required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave({
        agent: {
          agency: agency.trim(),
          contact: contact.trim(),
          email: email.trim(),
          phone: phone.trim(),
          address: address.trim(),
        },
        signer: { name: signerName.trim(), title: signerTitle.trim() },
        defaultBidBondPercent: pct,
        defaultWarrantyMonths: Math.round(months),
        defaultBondForm: bondForm.trim(),
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fin-modal-overlay" onClick={onClose}>
      <div
        className="fin-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Surety bonding defaults"
      >
        <div className="fin-modal-header">
          <h3>Surety bonding defaults</h3>
          <button className="fin-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="fin-modal-body">
          {error && <div className="fin-modal-error">{error}</div>}
          <p className="fin-modal-help">
            The bond agent is the addressee on every request letter. The signer and defaults
            prefill new requests; each letter can still be edited before it is saved.
          </p>

          <fieldset className="fin-modal-fieldset">
            <legend>Bond agent / broker <FieldHelp text="agent" /></legend>
            <div className="fin-modal-grid">
              <div className="fin-modal-field">
                <label className="fin-modal-label" htmlFor="sb-agency">Agency</label>
                <input id="sb-agency" className="fin-modal-input" value={agency} onChange={(e) => setAgency(e.target.value)} autoFocus />
              </div>
              <div className="fin-modal-field">
                <label className="fin-modal-label" htmlFor="sb-contact">Contact name</label>
                <input id="sb-contact" className="fin-modal-input" value={contact} onChange={(e) => setContact(e.target.value)} />
              </div>
              <div className="fin-modal-field">
                <label className="fin-modal-label" htmlFor="sb-email">Email</label>
                <input id="sb-email" className="fin-modal-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <div className="fin-modal-field">
                <label className="fin-modal-label" htmlFor="sb-phone">Phone</label>
                <input id="sb-phone" className="fin-modal-input" value={phone} onChange={(e) => setPhone(e.target.value)} />
              </div>
              <div className="fin-modal-field full">
                <label className="fin-modal-label" htmlFor="sb-address">Mailing address</label>
                <textarea
                  id="sb-address"
                  className="fin-modal-textarea"
                  rows={2}
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder={'Street\nCity, ST ZIP'}
                />
              </div>
            </div>
          </fieldset>

          <fieldset className="fin-modal-fieldset">
            <legend>Signer <FieldHelp text="signer" /></legend>
            <div className="fin-modal-grid">
              <div className="fin-modal-field">
                <label className="fin-modal-label" htmlFor="sb-signer">Name</label>
                <input id="sb-signer" className="fin-modal-input" value={signerName} onChange={(e) => setSignerName(e.target.value)} />
              </div>
              <div className="fin-modal-field">
                <label className="fin-modal-label" htmlFor="sb-signer-title">Title</label>
                <input id="sb-signer-title" className="fin-modal-input" value={signerTitle} onChange={(e) => setSignerTitle(e.target.value)} />
              </div>
            </div>
          </fieldset>

          <fieldset className="fin-modal-fieldset">
            <legend>Defaults</legend>
            <div className="fin-modal-grid">
              <div className="fin-modal-field">
                <label className="fin-modal-label" htmlFor="sb-bid-pct">Bid bond % <FieldHelp text="defaultBidBondPercent" /></label>
                <input
                  id="sb-bid-pct"
                  className="fin-modal-input"
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={bidPercent}
                  onChange={(e) => setBidPercent(e.target.value)}
                />
                <span className="fin-modal-help">Percent of the bid. Performance and payment bonds are always 100%.</span>
              </div>
              <div className="fin-modal-field">
                <label className="fin-modal-label" htmlFor="sb-warranty">Warranty months <FieldHelp text="defaultWarrantyMonths" align="right" /></label>
                <input
                  id="sb-warranty"
                  className="fin-modal-input"
                  type="number"
                  min="0"
                  step="1"
                  value={warrantyMonths}
                  onChange={(e) => setWarrantyMonths(e.target.value)}
                />
              </div>
              <div className="fin-modal-field full">
                <label className="fin-modal-label" htmlFor="sb-form">Bond form <FieldHelp text="defaultBondForm" /></label>
                <input
                  id="sb-form"
                  className="fin-modal-input"
                  value={bondForm}
                  onChange={(e) => setBondForm(e.target.value)}
                  placeholder="AIA A312"
                />
                <span className="fin-modal-help">The form the obligee usually requires — AIA A312, a state statutory form, or the owner&apos;s own form.</span>
              </div>
            </div>
          </fieldset>
        </div>

        <div className="fin-modal-footer">
          <div className="fin-modal-footer-right">
            <button type="button" className="fin-secondary-btn" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="button" className="fin-primary-btn" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save defaults'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuretyBondingSettingsModal;
